/**
 * `content/v1/` の読み込み(閲覧専用)。
 *
 * apps/web の `services/content.ts` と同じJSONを読むが、こちらは
 * **出題用の変換をしない**(engine に依存しない)。管理画面が要るのは
 * 「ステージ一覧 → 単語一覧」を人間が確認できる形にすることだけ。
 *
 * 取得元は同一オリジンの `/content/v1/`(dev は vite.config.ts のミドルウェア、
 * 本番は closeBundle で dist に同梱)。CORS を持ち込まないための選択。
 */

export type Language = 'japanese' | 'english';
export type StageCategory = 'practice' | 'training';

export const LANGUAGES: readonly Language[] = ['japanese', 'english'];

export const LANGUAGE_LABELS: Readonly<Record<Language, string>> = {
  japanese: '日本語(ローマ字)',
  english: '英語',
};

export const CATEGORY_LABELS: Readonly<Record<StageCategory, string>> = {
  practice: 'れんしゅう',
  training: 'トレーニング',
};

export interface WordMedia {
  readonly image?: string;
  readonly audio?: { readonly en?: string; readonly ja?: string };
  readonly images?: readonly string[];
  readonly audios?: { readonly en?: readonly string[]; readonly ja?: readonly string[] };
}

export interface ContentWord {
  readonly id: string;
  readonly display: string;
  readonly reading: string;
  readonly ruby?: string;
  readonly meaning?: Readonly<Record<string, string>>;
  readonly media?: WordMedia;
  readonly tags?: readonly string[];
  /** どの単語セットファイル由来か(管理画面での出所表示用)。 */
  readonly set?: string;
}

export interface WordSetFile {
  readonly schemaVersion: number;
  readonly set: string;
  readonly locale: string;
  readonly words: readonly ContentWord[];
}

export interface QuizConfig {
  readonly mode: string;
  readonly repeatCount?: number;
  readonly timeLimit?: number;
  readonly questionLimit?: number;
}

export interface StageUnlock {
  readonly requires: readonly string[];
  readonly minStars: number;
}

export interface StageDefinition {
  readonly id: string;
  readonly language: Language;
  readonly category: StageCategory;
  readonly section: string;
  readonly title: string;
  readonly titleSub?: string;
  readonly wordIds: readonly string[];
  readonly quiz: QuizConfig;
  readonly viewImage: boolean;
  readonly voice: boolean;
  readonly starCoins: readonly number[];
  readonly unlock?: StageUnlock;
  readonly gameType: string;
}

export interface StageSetFile {
  readonly schemaVersion: number;
  readonly language: Language;
  readonly wordSets: readonly string[];
  readonly stages: readonly StageDefinition[];
}

/** 1言語ぶんの閲覧素材(ステージ + 単語辞書 + 逆引き)。 */
export interface LanguageContent {
  readonly language: Language;
  readonly stages: readonly StageDefinition[];
  readonly wordSets: readonly string[];
  /** id → 単語。 */
  readonly wordsById: ReadonlyMap<string, ContentWord>;
  /** 単語id → その語を使っているステージid。「どこにも使われていない語」の検出用。 */
  readonly stageIdsByWordId: ReadonlyMap<string, readonly string[]>;
}

export class ContentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ContentError';
  }
}

// ── パース ────────────────────────────────────────────────
// 管理画面は「壊れたデータを見つける」道具でもあるので、パースは寛容にして
// 型が違うフィールドは落とすだけにする(1語壊れて全画面が落ちるのを避ける)。

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asStringArray(value: unknown): readonly string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((entry): entry is string => typeof entry === 'string');
}

function parseMedia(value: unknown): WordMedia | undefined {
  const raw = asRecord(value);
  if (!raw) return undefined;
  const image = asString(raw.image);
  const images = asStringArray(raw.images);
  const audioRaw = asRecord(raw.audio);
  const audiosRaw = asRecord(raw.audios);
  const audio = audioRaw
    ? {
        ...(asString(audioRaw.en) ? { en: asString(audioRaw.en) as string } : {}),
        ...(asString(audioRaw.ja) ? { ja: asString(audioRaw.ja) as string } : {}),
      }
    : undefined;
  const audios = audiosRaw
    ? {
        ...(asStringArray(audiosRaw.en)
          ? { en: asStringArray(audiosRaw.en) as readonly string[] }
          : {}),
        ...(asStringArray(audiosRaw.ja)
          ? { ja: asStringArray(audiosRaw.ja) as readonly string[] }
          : {}),
      }
    : undefined;
  return {
    ...(image === undefined ? {} : { image }),
    ...(images === undefined ? {} : { images }),
    ...(audio === undefined ? {} : { audio }),
    ...(audios === undefined ? {} : { audios }),
  };
}

export function parseWordSet(raw: unknown): WordSetFile {
  const root = asRecord(raw);
  if (!root) throw new ContentError('単語セット: オブジェクトではありません');
  const set = asString(root.set) ?? '';
  const list = Array.isArray(root.words) ? root.words : [];
  const words: ContentWord[] = [];
  for (const entry of list) {
    const w = asRecord(entry);
    if (!w) continue;
    const id = asString(w.id);
    const display = asString(w.display);
    if (id === undefined || display === undefined) continue;
    const ruby = asString(w.ruby);
    const meaning = asRecord(w.meaning) as Record<string, string> | null;
    const media = parseMedia(w.media);
    const tags = asStringArray(w.tags);
    words.push({
      id,
      display,
      reading: asString(w.reading) ?? display,
      ...(ruby === undefined ? {} : { ruby }),
      ...(meaning === null ? {} : { meaning }),
      ...(media === undefined ? {} : { media }),
      ...(tags === undefined ? {} : { tags }),
      ...(set === '' ? {} : { set }),
    });
  }
  return {
    schemaVersion: typeof root.schemaVersion === 'number' ? root.schemaVersion : 0,
    set,
    locale: asString(root.locale) ?? '',
    words,
  };
}

export function parseStageSet(raw: unknown): StageSetFile {
  const root = asRecord(raw);
  if (!root) throw new ContentError('ステージセット: オブジェクトではありません');
  const language = asString(root.language);
  if (language !== 'japanese' && language !== 'english') {
    throw new ContentError(`ステージセット: 未知の言語 "${String(root.language)}"`);
  }
  const list = Array.isArray(root.stages) ? root.stages : [];
  const stages: StageDefinition[] = [];
  for (const entry of list) {
    const s = asRecord(entry);
    if (!s) continue;
    const id = asString(s.id);
    if (id === undefined) continue;
    const quizRaw = asRecord(s.quiz) ?? {};
    const unlockRaw = asRecord(s.unlock);
    const titleSub = asString(s.titleSub);
    const category = asString(s.category);
    const numeric = (key: string): number | undefined =>
      typeof quizRaw[key] === 'number' ? (quizRaw[key] as number) : undefined;
    const repeatCount = numeric('repeatCount');
    const timeLimit = numeric('timeLimit');
    const questionLimit = numeric('questionLimit');
    stages.push({
      id,
      language,
      category: category === 'practice' || category === 'training' ? category : 'training',
      section: asString(s.section) ?? '(セクション未設定)',
      title: asString(s.title) ?? id,
      ...(titleSub === undefined ? {} : { titleSub }),
      wordIds: asStringArray(s.wordIds) ?? [],
      quiz: {
        mode: asString(quizRaw.mode) ?? 'sequential',
        ...(repeatCount === undefined ? {} : { repeatCount }),
        ...(timeLimit === undefined ? {} : { timeLimit }),
        ...(questionLimit === undefined ? {} : { questionLimit }),
      },
      viewImage: s.viewImage === true,
      voice: s.voice === true,
      starCoins: Array.isArray(s.starCoins)
        ? s.starCoins.map((n) => (typeof n === 'number' ? n : 0))
        : [],
      ...(unlockRaw === null
        ? {}
        : {
            unlock: {
              requires: asStringArray(unlockRaw.requires) ?? [],
              minStars: typeof unlockRaw.minStars === 'number' ? unlockRaw.minStars : 0,
            },
          }),
      gameType: asString(s.gameType) ?? 'standard',
    });
  }
  return {
    schemaVersion: typeof root.schemaVersion === 'number' ? root.schemaVersion : 0,
    language,
    wordSets: asStringArray(root.wordSets) ?? [],
    stages,
  };
}

/** ステージ側から単語の逆引きを作る。 */
export function buildStageIndex(
  stages: readonly StageDefinition[],
): ReadonlyMap<string, readonly string[]> {
  const index = new Map<string, string[]>();
  for (const stage of stages) {
    for (const wordId of stage.wordIds) {
      const list = index.get(wordId);
      if (list) list.push(stage.id);
      else index.set(wordId, [stage.id]);
    }
  }
  return index;
}

/** ステージを section 単位にまとめる(出現順を保つ)。 */
export interface StageSection {
  readonly section: string;
  readonly category: StageCategory;
  readonly stages: readonly StageDefinition[];
}

export function groupBySection(stages: readonly StageDefinition[]): readonly StageSection[] {
  const order: string[] = [];
  const buckets = new Map<string, StageDefinition[]>();
  for (const stage of stages) {
    const key = `${stage.category} ${stage.section}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      order.push(key);
    }
    bucket.push(stage);
  }
  return order.map((key) => {
    const [category, section] = key.split(' ') as [StageCategory, string];
    return { section, category, stages: buckets.get(key) ?? [] };
  });
}

/** 訳(第一言語=日本語優先)を1つ取り出す。 */
export function meaningText(word: ContentWord): string | null {
  const meaning = word.meaning;
  if (!meaning) return null;
  const ja = meaning.ja;
  if (ja) return ja;
  const first = Object.values(meaning).find((value) => value.length > 0);
  return first ?? null;
}

/** 単語の検索対象テキスト。 */
function haystack(word: ContentWord): string {
  return [
    word.id,
    word.display,
    word.reading,
    word.ruby ?? '',
    ...(word.meaning ? Object.values(word.meaning) : []),
    ...(word.tags ?? []),
  ]
    .join(' ')
    .toLowerCase();
}

/** 語・訳・タグ・id を対象にした素朴な部分一致検索。 */
export function searchWords(
  words: readonly ContentWord[],
  query: string,
  limit = 200,
): readonly ContentWord[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];
  const hits: ContentWord[] = [];
  for (const word of words) {
    if (haystack(word).includes(needle)) {
      hits.push(word);
      if (hits.length >= limit) break;
    }
  }
  return hits;
}

/** ステージ名・id・セクションを対象にした部分一致検索。 */
export function searchStages(
  stages: readonly StageDefinition[],
  query: string,
): readonly StageDefinition[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return stages;
  return stages.filter((stage) =>
    [stage.id, stage.title, stage.titleSub ?? '', stage.section]
      .join(' ')
      .toLowerCase()
      .includes(needle),
  );
}

/** タグの出現回数(テーマ横断の一覧用)。多い順。 */
export function tagCounts(words: readonly ContentWord[]): readonly (readonly [string, number])[] {
  const counts = new Map<string, number>();
  for (const word of words) {
    for (const tag of word.tags ?? []) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

// ── 取得 ──────────────────────────────────────────────────

export type FetchLike = (
  input: string,
) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

export interface ContentLoaderOptions {
  readonly baseUrl?: string;
  readonly fetchFn?: FetchLike;
}

export class ContentLoader {
  private readonly baseUrl: string;
  private readonly fetchFn: FetchLike;
  private readonly cache = new Map<Language, Promise<LanguageContent>>();

  constructor(options: ContentLoaderOptions = {}) {
    this.baseUrl = options.baseUrl ?? '/content/v1/';
    this.fetchFn = options.fetchFn ?? ((url) => fetch(url));
  }

  private async getJson(relative: string): Promise<unknown> {
    const url = `${this.baseUrl}${relative}`;
    let response;
    try {
      response = await this.fetchFn(url);
    } catch (cause) {
      throw new ContentError(`${url} の取得に失敗しました: ${String(cause)}`);
    }
    if (!response.ok) {
      throw new ContentError(`${url} の取得に失敗しました (HTTP ${response.status})`);
    }
    return response.json();
  }

  /** 1言語ぶんをまとめて読む(ステージ + そのステージが使う全単語セット)。 */
  load(language: Language): Promise<LanguageContent> {
    const cached = this.cache.get(language);
    if (cached) return cached;
    const promise = (async (): Promise<LanguageContent> => {
      const stageSet = parseStageSet(await this.getJson(`stages/${language}.json`));
      const sets = await Promise.all(
        stageSet.wordSets.map(async (set) => parseWordSet(await this.getJson(`words/${set}.json`))),
      );
      const wordsById = new Map<string, ContentWord>();
      for (const file of sets) {
        for (const word of file.words) wordsById.set(word.id, word);
      }
      return {
        language,
        stages: stageSet.stages,
        wordSets: stageSet.wordSets,
        wordsById,
        stageIdsByWordId: buildStageIndex(stageSet.stages),
      };
    })().catch((error: unknown) => {
      this.cache.delete(language);
      throw error;
    });
    this.cache.set(language, promise);
    return promise;
  }
}
